import {
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type { DonationTemplate, DonationBlock } from '@/types/donation';

// ============================================
// PATH HELPERS
// ============================================

const getTemplatesPath = () => `artifacts/${appId}/donation_templates`;
const getTemplatePath = (templateId: string) =>
  `artifacts/${appId}/donation_templates/${templateId}`;

// ============================================
// CRUD OPERATIONS
// ============================================

/**
 * Get all donation templates.
 */
export async function getDonationTemplates(): Promise<DonationTemplate[]> {
  const templatesRef = collection(db, getTemplatesPath());
  const q = query(templatesRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as DonationTemplate[];
}

/**
 * Get a single donation template by ID.
 */
export async function getDonationTemplate(templateId: string): Promise<DonationTemplate | null> {
  const templateRef = doc(db, getTemplatePath(templateId));
  const snapshot = await getDoc(templateRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as DonationTemplate;
}

/**
 * Subscribe to all donation templates.
 */
export function subscribeToDonationTemplates(
  callback: (templates: DonationTemplate[]) => void
): () => void {
  const templatesRef = collection(db, getTemplatesPath());
  const q = query(templatesRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const templates = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as DonationTemplate[];
    callback(templates);
  });
}

/**
 * Create a new donation template.
 */
export async function createDonationTemplate(
  name: string,
  blocks: DonationBlock[],
  userId: string,
  description?: string
): Promise<string> {
  const templatesRef = collection(db, getTemplatesPath());

  const template = {
    name,
    description,
    blocks,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  };

  const docRef = await addDoc(templatesRef, template);
  return docRef.id;
}

/**
 * Update an existing donation template.
 */
export async function updateDonationTemplate(
  templateId: string,
  updates: {
    name?: string;
    description?: string;
    blocks?: DonationBlock[];
  },
  userId: string
): Promise<void> {
  const templateRef = doc(db, getTemplatePath(templateId));

  await updateDoc(templateRef, {
    ...updates,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

/**
 * Delete a donation template.
 */
export async function deleteDonationTemplate(templateId: string): Promise<void> {
  const templateRef = doc(db, getTemplatePath(templateId));
  await deleteDoc(templateRef);
}
